import React, { useState, useEffect } from "react";
import { createPortal } from "react-dom";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import {
  FaTimes,
  FaCheckCircle,
  FaClock,
  FaTimesCircle,
  FaBuilding,
  FaProjectDiagram,
  FaUser,
  FaPhoneAlt,
  FaCalendarAlt,
  FaExternalLinkAlt,
  FaLayerGroup,
  FaFileAlt,
  FaTools,
  FaUserEdit,
  FaLaptopCode,
  FaExchangeAlt,
  FaHeadset,
} from "react-icons/fa";

const formatDate = (dateString) => {
  if (!dateString) return "N/A";
  const date = new Date(dateString);
  if (isNaN(date.getTime())) return "N/A";
  const day = String(date.getDate()).padStart(2, "0");
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const year = date.getFullYear();
  return `${day}/${month}/${year}`;
};

const formatTime = (dateString, item = null) => {
  if (dateString) {
    const date = new Date(dateString);
    if (!isNaN(date.getTime())) {
      const isMidnightUTC =
        date.getUTCHours() === 0 &&
        date.getUTCMinutes() === 0 &&
        date.getUTCSeconds() === 0;
      if (!isMidnightUTC) {
        let hours = date.getHours();
        const minutes = String(date.getMinutes()).padStart(2, "0");
        const ampm = hours >= 12 ? "PM" : "AM";
        hours = hours % 12 || 12;
        return `${hours}:${minutes} ${ampm}`;
      }
    }
  }

  // Fallback to ObjectId creation time
  if (item) {
    const idStr = String(item._id || item.id || "");
    if (idStr.length === 24) {
      try {
        const timeFromId = parseInt(idStr.substring(0, 8), 16) * 1000;
        if (!isNaN(timeFromId) && timeFromId > 0) {
          const d = new Date(timeFromId);
          let hours = d.getHours();
          const minutes = String(d.getMinutes()).padStart(2, "0");
          const ampm = hours >= 12 ? "PM" : "AM";
          hours = hours % 12 || 12;
          return `${hours}:${minutes} ${ampm}`;
        }
      } catch (e) {}
    }
  }

  return "N/A";
};

const calculateDuration = (startDate, endDate, status) => {
  if (status?.toLowerCase() !== "completed") return null;
  if (!startDate || !endDate) return null;
  const start = new Date(startDate);
  const end = new Date(endDate);
  if (isNaN(start) || isNaN(end) || end < start) return null;
  const diffMs = end - start;
  const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));
  const diffHrs = Math.floor((diffMs / (1000 * 60 * 60)) % 24);
  const diffMins = Math.floor((diffMs / (1000 * 60)) % 60);

  const parts = [];
  if (diffDays > 0) parts.push(`${diffDays}d`);
  if (diffHrs > 0) parts.push(`${diffHrs}h`);
  if (diffMins > 0 || parts.length === 0) parts.push(`${diffMins}m`);
  return parts.join(" ");
};

const getStatusBadge = (status) => {
  const s = (status || "").toLowerCase();
  if (s === "completed") {
    return {
      bg: "bg-emerald-50 text-emerald-700 border-emerald-300",
      icon: <FaCheckCircle className="text-emerald-600" />,
      label: "Completed",
    };
  }
  if (s === "pending") {
    return {
      bg: "bg-rose-50 text-rose-700 border-rose-300",
      icon: <FaTimesCircle className="text-rose-600" />,
      label: "Pending",
    };
  }
  if (s.includes("accept") || s.includes("solve") || s.includes("review") || s.includes("running")) {
    return {
      bg: "bg-amber-50 text-amber-700 border-amber-300",
      icon: <FaClock className="text-amber-600" />,
      label: status || "In Progress",
    };
  }
  return {
    bg: "bg-blue-50 text-blue-700 border-blue-300",
    icon: <FaClock className="text-blue-600" />,
    label: status || "Open",
  };
};

const CallDetailModal = ({ call, companyId, companyName, onClose }) => {
  const navigate = useNavigate();
  const [callData, setCallData] = useState(call);
  const [isLoadingFresh, setIsLoadingFresh] = useState(false);

  useEffect(() => {
    setCallData(call);
    if (call?._id) {
      let isMounted = true;
      const fetchFreshDetails = async () => {
        try {
          setIsLoadingFresh(true);
          const BaseUrl = process.env.REACT_APP_API_URL || "http://localhost:5000";
          const res = await axios.get(
            `${BaseUrl}/ConplainController/dfshfsdfgjftghjfghjgh/${call._id}`
          );
          if (isMounted && res.data?.data) {
            setCallData(res.data.data);
          }
        } catch (err) {
          console.error("Error fetching fresh call data in modal:", err);
        } finally {
          if (isMounted) setIsLoadingFresh(false);
        }
      };
      fetchFreshDetails();
      return () => {
        isMounted = false;
      };
    }
  }, [call]);

  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.key === "Escape") {
        onClose?.();
      }
    };
    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [onClose]);

  const [sidebarOffset, setSidebarOffset] = useState(0);

  useEffect(() => {
    const updateOffset = () => {
      const isDesktop = window.innerWidth >= 1024;
      if (!isDesktop) {
        setSidebarOffset(0);
        return;
      }
      const sidebarEl = document.querySelector(".Main_sidebar_bg_color");
      if (sidebarEl && sidebarEl.offsetParent !== null) {
        const rect = sidebarEl.getBoundingClientRect();
        if (rect.width > 0 && rect.right > 0) {
          setSidebarOffset(Math.round(rect.right + 16));
          return;
        }
      }
      setSidebarOffset(0);
    };

    updateOffset();
    window.addEventListener("resize", updateOffset);
    const interval = setInterval(updateOffset, 300);

    return () => {
      window.removeEventListener("resize", updateOffset);
      clearInterval(interval);
    };
  }, []);

  if (!callData) return null;

  const statusBadge = getStatusBadge(callData.Status);
  const duration = calculateDuration(callData.Date, callData.Complate_Date, callData.Status);

  const projectName =
    callData.Project?.project_name ||
    callData.Project?.ProjectName ||
    (typeof callData.Project === "string" ? callData.Project : "-");

  const moduleName =
    callData.Module?.ModuleName ||
    callData.Module?.module_name ||
    (typeof callData.Module === "string" ? callData.Module : "-");

  const typeName =
    callData.Type?.Type_name ||
    callData.Type?.TypeName ||
    callData.Type?.type_name ||
    (typeof callData.Type === "string" && callData.Type !== "-" ? callData.Type : null) ||
    callData.Requirement_Type ||
    "-";

  // Generator information
  const generatorObj = callData.InPutId;
  const generatorName =
    generatorObj?.Name ||
    generatorObj?.OwnerName ||
    (typeof generatorObj === "string" ? generatorObj : null) ||
    callData.Employee ||
    "Not Specified";
  const generatorRole = generatorObj?.Role || null;

  // Assigned Support Team member
  const supportObj = callData.SuportTeamId;
  const supportStaffName =
    supportObj?.Name ||
    supportObj?.OwnerName ||
    (typeof supportObj === "string" ? supportObj : null);
  const supportStaffRole = supportObj?.Role || null;

  // Senior Developer assigned (if any)
  const seniorDevObj = callData.SeniorDevloperID;
  const seniorDevName =
    seniorDevObj?.Name ||
    seniorDevObj?.OwnerName ||
    (typeof seniorDevObj === "string" ? seniorDevObj : null);
  const seniorDevStatus = callData.SeniorDevloperStatus || null;

  // Forwarding / Assignment history logs
  const recordLogs = Array.isArray(callData.Record) ? callData.Record : [];

  const resolvedCompany =
    callData.CompanyName?.CompanyName ||
    callData.CompanyName?.WhaClientName ||
    companyName ||
    "Company";

  const handleOpenFullPage = () => {
    if (callData._id && companyId) {
      navigate(`/partycalldetails/${callData._id}/${companyId}`);
    }
  };

  // Requirement, Present Status, Solution, Benefit data resolution
  const presentStatusText = (callData.PresentStatus || "").trim();
  const requirementText = (callData.Requirement || "").trim();
  const solutionText = (callData.Solution || "").trim();
  const benefitText = (callData.Benefit || "").trim();

  const hasPresentStatus = Boolean(presentStatusText && presentStatusText !== "-");
  const hasRequirement = Boolean(requirementText && requirementText !== "-");
  const hasSolution = Boolean(solutionText && solutionText !== "-" && solutionText.toLowerCase() !== "null");
  const hasBenefit = Boolean(benefitText && benefitText !== "-" && benefitText.toLowerCase() !== "null");

  // Check if both exist and are identical
  const areSameDescription =
    hasPresentStatus &&
    hasRequirement &&
    presentStatusText.toLowerCase() === requirementText.toLowerCase();

  const modalContent = (
    <div
      onClick={onClose}
      style={{
        paddingLeft: sidebarOffset > 0 ? `${sidebarOffset}px` : undefined,
      }}
      className="fixed inset-0 z-[45] flex items-center justify-center p-3 sm:p-4 bg-slate-900/75 backdrop-blur-[2px] animate__animated animate__fadeIn animate__faster cursor-pointer"
    >
      <div
        className="bg-white w-full max-w-2xl rounded-2xl shadow-2xl border border-slate-200 overflow-hidden flex flex-col max-h-[92vh] cursor-default"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="bg-[#154D75] text-white px-5 sm:px-6 py-3.5 flex items-center justify-between shadow-md">
          <div className="flex items-center gap-3">
            <span className="bg-white/20 text-white font-bold px-3 py-1 rounded-lg text-sm tracking-wider">
              CALL #{callData.CallNo || "N/A"}
            </span>
            <div
              className={`inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-semibold border ${statusBadge.bg}`}
            >
              {statusBadge.icon}
              <span>{statusBadge.label}</span>
            </div>
            {isLoadingFresh && (
              <span className="text-[11px] text-white/70 italic flex items-center gap-1">
                <span className="w-2 h-2 rounded-full bg-cyan-300 animate-ping"></span>
                Updating...
              </span>
            )}
          </div>
          <button
            onClick={onClose}
            className="text-white/80 hover:text-white p-1.5 rounded-lg hover:bg-white/10 transition-colors cursor-pointer"
            title="Close"
          >
            <FaTimes className="text-lg" />
          </button>
        </div>

        {/* Modal Body */}
        <div className="p-5 sm:p-6 overflow-y-auto space-y-4 text-sm">
          {/* Company & Generation Summary Box */}
          <div className="bg-slate-50/90 rounded-xl p-4 border border-slate-200/90 grid grid-cols-1 sm:grid-cols-2 gap-3.5 shadow-2xs">
            {/* Company Name */}
            <div className="flex items-start gap-2.5">
              <div className="w-8 h-8 rounded-lg bg-blue-100/80 text-[#154D75] flex items-center justify-center shrink-0 mt-0.5">
                <FaBuilding className="text-sm" />
              </div>
              <div className="min-w-0">
                <p className="text-[11px] text-slate-500 font-semibold uppercase tracking-wider">
                  Company Name
                </p>
                <p className="font-bold text-slate-800 text-sm truncate" title={resolvedCompany}>
                  {resolvedCompany}
                </p>
              </div>
            </div>

            {/* Generated By (किसने Generate किया) */}
            <div className="flex items-start gap-2.5">
              <div className="w-8 h-8 rounded-lg bg-indigo-100/80 text-indigo-700 flex items-center justify-center shrink-0 mt-0.5">
                <FaUserEdit className="text-sm" />
              </div>
              <div className="min-w-0">
                <p className="text-[11px] text-slate-500 font-semibold uppercase tracking-wider">
                  Generated By
                </p>
                <div className="flex items-center gap-1.5 flex-wrap">
                  <span className="font-bold text-slate-800 text-sm">
                    {generatorName}
                  </span>
                  {generatorRole && (
                    <span className="text-[10px] px-2 py-0.2 bg-indigo-100 text-indigo-700 font-semibold rounded-full border border-indigo-200">
                      {generatorRole}
                    </span>
                  )}
                </div>
              </div>
            </div>

            {/* Generated Date & Time (कब किया) */}
            <div className="flex items-start gap-2.5">
              <div className="w-8 h-8 rounded-lg bg-emerald-100/80 text-emerald-700 flex items-center justify-center shrink-0 mt-0.5">
                <FaCalendarAlt className="text-sm" />
              </div>
              <div>
                <p className="text-[11px] text-slate-500 font-semibold uppercase tracking-wider">
                  Generated Date & Time
                </p>
                <p className="font-bold text-slate-800 text-sm">
                  {formatDate(callData.Date)}{" "}
                  <span className="text-slate-500 font-normal">at</span>{" "}
                  {formatTime(callData.Date, callData)}
                </p>
              </div>
            </div>

            {/* Completed Date / Duration */}
            {callData.Complate_Date ? (
              <div className="flex items-start gap-2.5">
                <div className="w-8 h-8 rounded-lg bg-teal-100/80 text-teal-700 flex items-center justify-center shrink-0 mt-0.5">
                  <FaCheckCircle className="text-sm" />
                </div>
                <div>
                  <p className="text-[11px] text-slate-500 font-semibold uppercase tracking-wider">
                    Completed Date & Duration
                  </p>
                  <p className="font-bold text-emerald-700 text-sm">
                    {formatDate(callData.Complate_Date)} at {formatTime(callData.Complate_Date)}
                  </p>
                  {duration && (
                    <p className="text-[11px] text-slate-500 font-medium">
                      Duration: <span className="font-semibold text-slate-700">{duration}</span>
                    </p>
                  )}
                </div>
              </div>
            ) : (
              <div className="flex items-start gap-2.5">
                <div className="w-8 h-8 rounded-lg bg-amber-100/80 text-amber-700 flex items-center justify-center shrink-0 mt-0.5">
                  <FaClock className="text-sm" />
                </div>
                <div>
                  <p className="text-[11px] text-slate-500 font-semibold uppercase tracking-wider">
                    Call Status
                  </p>
                  <p className="font-bold text-amber-700 text-sm">
                    {callData.Status || "Pending"}
                  </p>
                </div>
              </div>
            )}
          </div>

          {/* Assignment & Project Attributes Grid */}
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-2.5">
            <div className="p-3 bg-slate-50 rounded-xl border border-slate-200 shadow-2xs min-w-0">
              <p className="text-[11px] text-slate-500 font-semibold flex items-center gap-1 mb-1">
                <FaProjectDiagram className="text-[#154D75]" /> Project
              </p>
              <p className="font-bold text-slate-800 text-sm truncate" title={projectName}>
                {projectName}
              </p>
            </div>

            <div className="p-3 bg-slate-50 rounded-xl border border-slate-200 shadow-2xs min-w-0">
              <p className="text-[11px] text-slate-500 font-semibold flex items-center gap-1 mb-1">
                <FaLayerGroup className="text-[#154D75]" /> Module
              </p>
              <p className="font-bold text-slate-800 text-sm truncate" title={moduleName}>
                {moduleName}
              </p>
            </div>

            <div className="p-3 bg-slate-50 rounded-xl border border-slate-200 shadow-2xs min-w-0">
              <p className="text-[11px] text-slate-500 font-semibold flex items-center gap-1 mb-1">
                <FaFileAlt className="text-[#154D75]" /> Type
              </p>
              <p className="font-bold text-slate-800 text-sm truncate" title={typeName}>
                {typeName}
              </p>
              {callData.Requirement_Type && callData.Requirement_Type !== typeName && (
                <p className="text-[10px] text-slate-500 font-medium truncate mt-0.5" title={callData.Requirement_Type}>
                  ({callData.Requirement_Type})
                </p>
              )}
            </div>

            {/* Assigned Support Staff Card */}
            <div className={`p-3 rounded-xl border shadow-2xs min-w-0 ${supportStaffName ? "bg-emerald-50/70 border-emerald-200" : "bg-slate-50 border-slate-200"}`}>
              <p className="text-[11px] font-semibold flex items-center gap-1 mb-1 text-slate-600">
                <FaHeadset className={supportStaffName ? "text-emerald-600" : "text-slate-400"} />{" "}
                {supportStaffRole?.toLowerCase().includes("support") ? "Assigned Support Team" : "Assigned Employee"}
              </p>
              {supportStaffName ? (
                <div className="min-w-0">
                  <p className="font-bold text-emerald-900 text-sm truncate leading-snug" title={supportStaffName}>
                    {supportStaffName}
                  </p>
                  {supportStaffRole && (
                    <p className="text-[10px] text-emerald-700 font-medium truncate mt-0.5" title={supportStaffRole}>
                      {supportStaffRole}
                    </p>
                  )}
                </div>
              ) : (
                <span className="inline-block text-xs font-semibold text-rose-600 bg-rose-50 px-2 py-0.5 rounded border border-rose-200">
                  Unassigned
                </span>
              )}
            </div>
          </div>

          {/* Senior Developer Assignment Card if assigned */}
          {seniorDevName && (
            <div className="p-3 bg-purple-50/70 rounded-xl border border-purple-200/80 flex items-center justify-between text-xs shadow-2xs">
              <div className="flex items-center gap-2.5">
                <div className="w-7 h-7 rounded-lg bg-purple-100 text-purple-700 flex items-center justify-center shrink-0">
                  <FaLaptopCode className="text-xs" />
                </div>
                <div>
                  <span className="text-slate-500 font-medium">Assigned Senior Developer:</span>
                  <span className="font-bold text-purple-900 ml-1.5">{seniorDevName}</span>
                </div>
              </div>
              {seniorDevStatus && (
                <span className="px-2.5 py-0.5 bg-purple-100 text-purple-800 text-[11px] font-semibold rounded-full border border-purple-200">
                  Status: {seniorDevStatus}
                </span>
              )}
            </div>
          )}

          {/* Contact Person if available */}
          {(callData.ContactPerName || callData.ContactPer) && (
            <div className="p-3 bg-blue-50/50 rounded-xl border border-blue-100 flex items-center justify-between text-xs">
              <div className="flex items-center gap-2">
                <FaUser className="text-[#154D75]" />
                <span className="text-slate-600">Contact:</span>
                <span className="font-semibold text-slate-800">
                  {callData.ContactPerName || "N/A"}
                </span>
              </div>
              {callData.ContactPer && (
                <div className="flex items-center gap-1.5 text-slate-700">
                  <FaPhoneAlt className="text-slate-500 text-[11px]" />
                  <span className="font-medium">{callData.ContactPer}</span>
                </div>
              )}
            </div>
          )}

          {/* Assignment & Forwarding History (अगर किसी को आगे Assign / Forward किया है) */}
          {recordLogs.length > 0 && (
            <div className="space-y-2 pt-1">
              <h4 className="text-xs font-bold uppercase tracking-wider text-slate-700 flex items-center gap-2">
                <FaExchangeAlt className="text-[#154D75]" />
                <span>Assignment & Forwarding History</span>
                <span className="text-[10px] bg-blue-100 text-[#154D75] px-2 py-0.5 rounded-full font-bold">
                  {recordLogs.length} {recordLogs.length === 1 ? "Record" : "Records"}
                </span>
              </h4>

              <div className="bg-slate-50/90 rounded-xl p-3 sm:p-3.5 border border-slate-200 space-y-2.5 shadow-2xs">
                {recordLogs.map((rec, idx) => {
                  const staffName =
                    rec.InHouseId?.Name ||
                    rec.InHouseId?.OwnerName ||
                    (typeof rec.InHouseId === "string" ? rec.InHouseId : "Support Team Member");
                  const staffRole = rec.InHouseId?.Role;
                  const logDate = formatDate(rec.Date);
                  const logTime = formatTime(rec.Date);
                  const isLast = idx === recordLogs.length - 1;

                  return (
                    <div key={rec._id || idx} className="flex items-start gap-2.5 text-xs">
                      {/* Step Indicator */}
                      <div className="flex flex-col items-center shrink-0 mt-0.5">
                        <div
                          className={`w-5 h-5 rounded-full flex items-center justify-center text-[10px] font-bold ${isLast
                            ? "bg-[#154D75] text-white"
                            : "bg-slate-200 text-slate-700"
                            }`}
                        >
                          {idx + 1}
                        </div>
                        {!isLast && <div className="w-0.5 h-4 bg-slate-300 mt-1" />}
                      </div>

                      {/* Content Card */}
                      <div className="flex-1 bg-white p-2.5 rounded-lg border border-slate-200 shadow-2xs">
                        <div className="flex flex-wrap items-center justify-between gap-1.5">
                          <div className="flex items-center gap-1.5 flex-wrap">
                            <span className="text-slate-500 font-medium">Forwarded / Assigned To:</span>
                            <span className="font-bold text-slate-800">{staffName}</span>
                            {rec.Status && (
                              <span className="text-[10px] bg-blue-50 text-blue-700 font-bold px-2 py-0.5 rounded-full border border-blue-200">
                                {rec.Status}
                              </span>
                            )}
                            {staffRole && (
                              <span className="text-[10px] bg-slate-100 text-slate-700 px-1.5 py-0.2 rounded border border-slate-200">
                                {staffRole}
                              </span>
                            )}
                          </div>
                          {(logDate !== "N/A" || logTime !== "N/A") && (
                            <span className="text-[11px] text-slate-500 font-medium flex items-center gap-1">
                              <FaClock className="text-[10px]" />
                              {logDate} {logTime !== "N/A" ? `at ${logTime}` : ""}
                            </span>
                          )}
                        </div>
                        {rec.DescripTione && (
                          <p className="text-slate-600 text-[11px] bg-slate-50 p-1.5 rounded border border-slate-100 mt-1.5 leading-relaxed">
                            {rec.DescripTione}
                          </p>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          {/* Requirement / Issue Description Section */}
          <div className="space-y-1.5">
            <h4 className="text-xs font-bold uppercase tracking-wider text-slate-600 flex items-center gap-2">
              <span className="w-2 h-2 rounded-full bg-[#154D75]"></span>
              Requirement / Issue Description
            </h4>
            <div className="bg-slate-50 p-3.5 rounded-xl border border-slate-200 text-slate-800 leading-relaxed whitespace-pre-wrap text-sm">
              {hasPresentStatus && hasRequirement && !areSameDescription ? (
                <div className="space-y-2.5">
                  <div>
                    <span className="text-[11px] font-bold text-slate-500 uppercase tracking-wider block mb-0.5">
                      Present Status / Issue:
                    </span>
                    <p className="text-slate-800 leading-relaxed whitespace-pre-wrap">{presentStatusText}</p>
                  </div>
                  <div className="border-t border-slate-200/80 pt-2">
                    <span className="text-[11px] font-bold text-slate-500 uppercase tracking-wider block mb-0.5">
                      Specific Requirement:
                    </span>
                    <p className="text-slate-800 leading-relaxed whitespace-pre-wrap">{requirementText}</p>
                  </div>
                </div>
              ) : (
                presentStatusText || requirementText || (
                  <span className="text-slate-400 italic">No requirement details provided.</span>
                )
              )}
            </div>
          </div>

          {/* Solution Section */}
          {hasSolution && (
            <div className="space-y-1.5">
              <h4 className="text-xs font-bold uppercase tracking-wider text-emerald-700 flex items-center gap-2">
                <FaTools className="text-emerald-600" />
                Solution / Action Taken
              </h4>
              <div className="bg-emerald-50/70 p-3.5 rounded-xl border border-emerald-200 text-slate-800 leading-relaxed whitespace-pre-wrap text-sm">
                {solutionText}
              </div>
            </div>
          )}

          {/* Benefit if present */}
          {hasBenefit && (
            <div className="space-y-1.5">
              <h4 className="text-xs font-bold uppercase tracking-wider text-indigo-700 flex items-center gap-2">
                <span className="w-2 h-2 rounded-full bg-indigo-600"></span>
                Benefit
              </h4>
              <div className="bg-indigo-50/50 p-3.5 rounded-xl border border-indigo-100 text-slate-700 text-sm leading-relaxed whitespace-pre-wrap">
                {benefitText}
              </div>
            </div>
          )}

          {/* Photos / Attachments if any */}
          {Array.isArray(callData.Photos) && callData.Photos.filter(Boolean).length > 0 && (
            <div className="space-y-2">
              <h4 className="text-xs font-bold uppercase tracking-wider text-slate-600">
                Attachments ({callData.Photos.filter(Boolean).length})
              </h4>
              <div className="flex flex-wrap gap-2">
                {callData.Photos.filter(Boolean).map((photoUrl, idx) => (
                  <a
                    key={idx}
                    href={photoUrl}
                    target="_blank"
                    rel="noreferrer"
                    className="group relative block w-16 h-16 rounded-lg overflow-hidden border border-slate-200 hover:border-[#154D75] transition-all"
                  >
                    <img
                      src={photoUrl}
                      alt={`Attachment ${idx + 1}`}
                      className="w-full h-full object-cover group-hover:scale-105 transition-transform"
                    />
                  </a>
                ))}
              </div>
            </div>
          )}
        </div>

        {/* Footer Actions */}
        <div className="bg-slate-50 px-5 sm:px-6 py-3 border-t border-slate-200 flex items-center justify-between">
          {callData._id && companyId ? (
            <button
              onClick={handleOpenFullPage}
              className="inline-flex items-center gap-2 text-xs font-semibold text-[#154D75] hover:text-[#0f3b5e] hover:underline cursor-pointer"
            >
              <span>View Full Call Page</span>
              <FaExternalLinkAlt className="text-[10px]" />
            </button>
          ) : (
            <div></div>
          )}

          <button
            onClick={onClose}
            className="px-4 py-2 bg-slate-200 hover:bg-slate-300 text-slate-700 font-medium rounded-lg text-xs transition-colors cursor-pointer"
          >
            Close
          </button>
        </div>
      </div>
    </div>
  );

  return typeof document !== "undefined"
    ? createPortal(modalContent, document.body)
    : modalContent;
};

export default CallDetailModal;

